import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';

import { logger } from 'adapters/Logger';
import { getCheckoutSession } from 'services/repositories/sessions';

export type CheckoutSession = {
  name: string;
  email: string;
  phone: string;
  leadId: string;
  selectedPlan?: string;
  additionalData?: Record<string, unknown>;
};

type UseCheckoutSessionResult = {
  isLoading: boolean;
  sessionData: CheckoutSession | null;
};

export function useCheckoutSession(
  cartId: string | undefined
): UseCheckoutSessionResult {
  const [isLoading, setIsLoading] = useState<boolean>(!!cartId);
  const [sessionData, setSessionData] = useState<CheckoutSession | null>(null);

  useEffect(() => {
    if (!cartId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getCheckoutSession(cartId)
      .then((data) => {
        if (!cancelled) {
          setSessionData(data);
        }
      })
      .catch((err: unknown) => {
        if (
          !cancelled &&
          !(isAxiosError(err) && err.response?.status === 404)
        ) {
          const axiosFailure = isAxiosError(err) ? err : undefined;
          logger.error(
            'lead_session.fetch_error',
            {
              flow: 'checkout-session',
              operation: 'redis-recovery',
              ...(axiosFailure?.code ? { errorCode: axiosFailure.code } : {}),
            },
            {
              cartId,
              ...(err instanceof Error ? { errorMessage: err.message } : {}),
              ...(axiosFailure?.response?.status
                ? { statusCode: axiosFailure.response.status }
                : {}),
              ...(typeof document !== 'undefined'
                ? { visibilityState: document.visibilityState }
                : {}),
              ...(typeof navigator !== 'undefined'
                ? { onLine: navigator.onLine }
                : {}),
            }
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cartId]);

  return { isLoading, sessionData };
}
